import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { payrollEngineApi } from '../../../services/api';
import { Spinner } from '../../../components/shared/index';

const FEDERAL_STATUSES = [
  { value: 'single',              label: 'Single' },
  { value: 'married_jointly',     label: 'Married filing jointly' },
  { value: 'married_separately',  label: 'Married filing separately' },
  { value: 'head_of_household',   label: 'Head of household' },
];

const STATE_STATUSES = [
  { value: 'single',            label: 'Single' },
  { value: 'married',           label: 'Married' },
  { value: 'head_of_household', label: 'Head of household' },
];

const EMPTY = {
  federal_filing_status:   'single',
  multiple_jobs_checkbox:  false,
  dependents_credit:       '',
  other_income:            '',
  deductions_adjustment:   '',
  extra_withholding:       '',
  exempt_federal:          false,
  state_code:              '',
  state_filing_status:     'single',
  state_allowances:        '',
  state_extra_withholding: '',
  exempt_state:            false,
  resident_state_code:     '',
  work_state_code:         '',
};

function InfoBanner({ children }) {
  return (
    <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
      <InformationCircleIcon className="w-4 h-4 shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  );
}

function toNum(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export default function TaxProfileForm({ employeeId, canEdit = true }) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['tax-profile', employeeId],
    queryFn:  () => payrollEngineApi.getTaxProfile(employeeId).then(r => r.data),
    enabled:  !!employeeId,
  });

  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (data?.profile) {
      const p = data.profile;
      setForm({
        federal_filing_status:   p.federal_filing_status   ?? 'single',
        multiple_jobs_checkbox:  !!p.multiple_jobs_checkbox,
        dependents_credit:       p.dependents_credit       ?? '',
        other_income:            p.other_income            ?? '',
        deductions_adjustment:   p.deductions_adjustment   ?? '',
        extra_withholding:       p.extra_withholding       ?? '',
        exempt_federal:          !!p.exempt_federal,
        state_code:              p.state_code              ?? '',
        state_filing_status:     p.state_filing_status     ?? 'single',
        state_allowances:        p.state_allowances        ?? '',
        state_extra_withholding: p.state_extra_withholding ?? '',
        exempt_state:            !!p.exempt_state,
        resident_state_code:     p.resident_state_code     ?? '',
        work_state_code:         p.work_state_code         ?? '',
      });
    }
  }, [data]);

  const update = useMutation({
    mutationFn: (payload) => payrollEngineApi.updateTaxProfile(employeeId, payload),
    onSuccess: () => {
      toast.success('Tax profile saved');
      qc.invalidateQueries({ queryKey: ['tax-profile', employeeId] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to save tax profile'),
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    update.mutate({
      federal_filing_status:   form.federal_filing_status,
      multiple_jobs_checkbox:  !!form.multiple_jobs_checkbox,
      dependents_credit:       toNum(form.dependents_credit) ?? 0,
      other_income:            toNum(form.other_income) ?? 0,
      deductions_adjustment:   toNum(form.deductions_adjustment) ?? 0,
      extra_withholding:       toNum(form.extra_withholding) ?? 0,
      exempt_federal:          !!form.exempt_federal,
      state_code:              form.state_code || null,
      state_filing_status:     form.state_filing_status,
      state_allowances:        toNum(form.state_allowances) ?? 0,
      state_extra_withholding: toNum(form.state_extra_withholding) ?? 0,
      exempt_state:            !!form.exempt_state,
      resident_state_code:     form.resident_state_code || null,
      work_state_code:         form.work_state_code || null,
    });
  };

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  const states = data?.supported_states || [];
  const disabled = !canEdit;

  return (
    <form onSubmit={submit} className="space-y-8">
      {/* Federal */}
      <section className="card p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="section-title">Federal (IRS Form W-4)</h2>
          <span className="text-xs text-gray-400">Annual figures unless noted</span>
        </div>

        <InfoBanner>
          Line 4c (extra withholding) is deducted <strong>every pay period</strong>, not annually.
          Lines 3, 4a, and 4b are <strong>annual</strong> amounts.
        </InfoBanner>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          <div>
            <label className="label">Filing status</label>
            <select
              className="input"
              value={form.federal_filing_status}
              onChange={(e) => set('federal_filing_status', e.target.value)}
              disabled={disabled}
            >
              {FEDERAL_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          <div className="flex items-end">
            <label className="flex items-center gap-2 pb-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={form.multiple_jobs_checkbox}
                onChange={(e) => set('multiple_jobs_checkbox', e.target.checked)}
                disabled={disabled}
              />
              Step 2(c): Multiple jobs / spouse works
            </label>
          </div>

          <div>
            <label className="label">Dependents credit (Step 3) — annual $</label>
            <input
              type="number" step="0.01" min="0"
              className="input"
              value={form.dependents_credit}
              onChange={(e) => set('dependents_credit', e.target.value)}
              disabled={disabled}
              placeholder="e.g. 2000"
            />
          </div>

          <div>
            <label className="label">Other income (Step 4a) — annual $</label>
            <input
              type="number" step="0.01" min="0"
              className="input"
              value={form.other_income}
              onChange={(e) => set('other_income', e.target.value)}
              disabled={disabled}
            />
          </div>

          <div>
            <label className="label">Deductions adjustment (Step 4b) — annual $</label>
            <input
              type="number" step="0.01" min="0"
              className="input"
              value={form.deductions_adjustment}
              onChange={(e) => set('deductions_adjustment', e.target.value)}
              disabled={disabled}
            />
          </div>

          <div>
            <label className="label">Extra withholding (Step 4c) — <strong>per pay period</strong> $</label>
            <input
              type="number" step="0.01" min="0"
              className="input"
              value={form.extra_withholding}
              onChange={(e) => set('extra_withholding', e.target.value)}
              disabled={disabled}
            />
          </div>

          <div className="md:col-span-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={form.exempt_federal}
                onChange={(e) => set('exempt_federal', e.target.checked)}
                disabled={disabled}
              />
              Exempt from federal income tax withholding
            </label>
          </div>
        </div>
      </section>

      {/* State */}
      <section className="card p-5">
        <h2 className="section-title mb-4">State withholding</h2>

        <InfoBanner>
          State rules vary. Select the state where you&rsquo;re withheld, then choose your state filing status and allowances.
        </InfoBanner>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          <div>
            <label className="label">State</label>
            <select
              className="input"
              value={form.state_code}
              onChange={(e) => set('state_code', e.target.value)}
              disabled={disabled}
            >
              <option value="">— Select state —</option>
              {states.map((s) => (
                <option key={s.code || s} value={s.code || s}>
                  {s.name ? `${s.name} (${s.code})` : s}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">State filing status</label>
            <select
              className="input"
              value={form.state_filing_status}
              onChange={(e) => set('state_filing_status', e.target.value)}
              disabled={disabled}
            >
              {STATE_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          <div>
            <label className="label">State allowances (integer)</label>
            <input
              type="number" step="1" min="0"
              className="input"
              value={form.state_allowances}
              onChange={(e) => set('state_allowances', e.target.value)}
              disabled={disabled}
            />
          </div>

          <div>
            <label className="label">State extra withholding — per pay period $</label>
            <input
              type="number" step="0.01" min="0"
              className="input"
              value={form.state_extra_withholding}
              onChange={(e) => set('state_extra_withholding', e.target.value)}
              disabled={disabled}
            />
          </div>

          <div className="md:col-span-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={form.exempt_state}
                onChange={(e) => set('exempt_state', e.target.checked)}
                disabled={disabled}
              />
              Exempt from state income tax withholding
            </label>
          </div>
        </div>
      </section>

      {/* Nexus */}
      <section className="card p-5">
        <h2 className="section-title mb-4">Nexus (resident vs. work state)</h2>

        <InfoBanner>
          If you live in one state and work in another, set both. Reciprocity agreements are handled by the engine based on these codes.
        </InfoBanner>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          <div>
            <label className="label">Resident state</label>
            <select
              className="input"
              value={form.resident_state_code}
              onChange={(e) => set('resident_state_code', e.target.value)}
              disabled={disabled}
            >
              <option value="">— Select state —</option>
              {states.map((s) => (
                <option key={s.code || s} value={s.code || s}>
                  {s.name ? `${s.name} (${s.code})` : s}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Work state</label>
            <select
              className="input"
              value={form.work_state_code}
              onChange={(e) => set('work_state_code', e.target.value)}
              disabled={disabled}
            >
              <option value="">— Select state —</option>
              {states.map((s) => (
                <option key={s.code || s} value={s.code || s}>
                  {s.name ? `${s.name} (${s.code})` : s}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {canEdit && (
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={update.isPending}
            className={clsx('btn-primary', update.isPending && 'opacity-50')}
          >
            {update.isPending ? 'Saving…' : 'Save tax profile'}
          </button>
        </div>
      )}
    </form>
  );
}
